import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, branch } from 'recompose';
import { registrationNumber as registrationNumberValidator } from '@klarna/mf-validations';
import { selectors as i18nSelectors } from 'mage-i18n';
// eslint-disable-next-line import/no-extraneous-dependencies
import { SpacerHorizontal, SpacerVertical, Typography } from '@klarna/bubble-ui';
import {
  defaultContainerPropTypes,
  FieldsetForObject,
  formUtils,
  HorizontalWrapper,
  Input,
  payloadUtils,
} from 'moet-core';

import { OPTIONS_META } from '@/constants/countries';
import { withChainedCapabilitiesBeforeNext } from '@/flow/common';
import { isRegistrationIdOptional } from '@/flow/configs/utils/field-validations';
import { shouldCollectDocuments } from '@/flow/configs/selectors/documents';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { logEvent } from '@/utils/analytics';
import { CAPS } from '@/constants';
import {
  BackButton,
  CallToActionButton,
  ContainerTemplate,
  ToggleWrappedContent,
} from '@/components';

import { ANALYTICS_EVENTS } from '../analyticsEvents';
import { getDefaultState } from '../selectors';
import { CY_COMPANY_REGISTRATION_ID_IDS } from '../constants';
import { countrySpecificTranslationParser } from '@/selectors/utils';
import { getMerchantAwareTranslator } from '@/selectors/merchant';

const tPrefix = 'products-fe.containers.optional_registration_information';
const tErrorsPrefix = 'products-fe.error_messages.validation';

export class OptionalCompanyRegistrationId extends React.Component {
  constructor(props) {
    super(props);

    const { country, defaultState, language, t } = props;
    const { toggleValue, registrationNumber = '' } = defaultState || {};
    const state = {
      language,
      toggleValue,
      fields: formUtils.initForm({
        registrationNumber: {
          type: 'text',
          component: Input,
          label: t(`${tPrefix}.fields.registration_number.label`, country),
          size: 1.0,
          validators: [
            {
              callback: registrationNumberValidator.validate,
              errorMessage: t(`${tErrorsPrefix}.registrationId.general`, country),
              options: { country },
            },
          ],
          extraProps: {
            autoFocus: true,
            tooltipText: t(
              OptionalCompanyRegistrationId.getTooltipMessage(registrationNumber, props),
            ),
            ...generateCyElemId(CY_COMPANY_REGISTRATION_ID_IDS.INPUT_REGISTRATION_NUMBER, window),
          },
          isInitialized: false,
          isValid: false,
          value: registrationNumber,
        },
      }),
    };

    this.state = { ...state, initialState: state, ctaClicked: false };

    this.onChangeFields = this.onChangeFields.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onNext = this.onNext.bind(this);
  }

  static updateTranslation(key, props) {
    const { tMerchantAware, country } = props;
    const registrationNumber = this?.state?.fields?.registrationNumber?.value || '';
    const fieldTranslationsMap = {
      registrationNumber: {
        label: `${tPrefix}.fields.registration_number.label`,
        validators: [
          {
            callback: registrationNumberValidator.validate,
            errorMessage: `${tErrorsPrefix}.registrationId.general`,
            options: { country },
          },
        ],
        extraProps: {
          autoFocus: true,
          tooltipText: tMerchantAware(
            OptionalCompanyRegistrationId.getTooltipMessage(registrationNumber, props),
          ),
          ...generateCyElemId(CY_COMPANY_REGISTRATION_ID_IDS.INPUT_REGISTRATION_NUMBER, window),
        },
      },
    };

    return formUtils.updateTranslatedFields(fieldTranslationsMap, key, tMerchantAware);
  }

  static getDerivedStateFromProps(props, state) {
    const { language } = props;
    if (state.language !== language) {
      const newState = formUtils.initFormFromState({
        ...state,
        language,
        fields: {
          ...state.fields,
          registrationNumber: {
            ...state.fields.registrationNumber,
            ...OptionalCompanyRegistrationId.updateTranslation('registrationNumber', props),
          },
        },
      });

      return {
        ...newState,
        initialState: state.initialState,
      };
    }

    return null;
  }

  onToggle(toggleValue) {
    this.setState({ toggleValue });
  }

  onChangeFields(fields) {
    const { t } = this.props;
    const { value } = fields.registrationNumber;
    fields.registrationNumber.extraProps.tooltipText = t(
      OptionalCompanyRegistrationId.getTooltipMessage(value, this.props),
    );
    this.setState({ fields });
  }

  onBlur(key) {
    formUtils.onBlurFields(key, this.state, this.onChangeFields);
  }

  onChange(key, val) {
    formUtils.onChangeFields(key, val, this.state, this.onChangeFields);
  }

  getBody(state) {
    return {
      registered: state.toggleValue,
      // To be able to clear this field, we should send an empty string to the MoMA PATCH request
      registrationId: state.toggleValue === true ? state.fields.registrationNumber.value : '',
    };
  }

  static getTooltipMessage(value, props) {
    const { country } = props;

    if (country === 'GB') {
      const isMissingLeadingZero = /^[1-9]{1}[0-9]{6}$/.test(value);

      if (isMissingLeadingZero) {
        return `${tErrorsPrefix}.registrationId.missing_leading_0_GB`;
      }
    }

    return `${tErrorsPrefix}.registrationId.invalid.${country}`;
  }

  onNext() {
    this.setState({
      ...this.state,
      fields: formUtils.initializeFields(this.state.fields),
      ctaClicked: true,
    });

    formUtils.callToActionOnNextWrapper(
      this.state.fields,
      () => {
        this.props.onNext(
          payloadUtils.getMerchantCapabilityPayload(
            this.props.mid,
            this.state,
            this.getBody,
            'OptionalCompanyRegistrationId',
          ),
        );
      },
      this.state.toggleValue === false ? true : undefined,
    );
  }

  render() {
    const { error, hasBack, isDisabled, onBack, t, tMerchantAware, isLoading } = this.props;
    const { fields, ctaClicked, toggleValue } = this.state;

    const tabIndex = (toggleValue ? Object.keys(fields).length : 0) + 1;

    const actionButtons = (
      <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
        {hasBack && (
          <>
            <BackButton tabIndex={tabIndex + 1} onClick={onBack} disabled={isDisabled}>
              {t(`${tPrefix}.buttons.back`)}
            </BackButton>
            <SpacerHorizontal medium />
          </>
        )}
        <CallToActionButton
          tabIndex={tabIndex}
          loading={isLoading}
          onClick={this.onNext}
          disabled={isDisabled}
          {...generateCyElemId(CY_COMPANY_REGISTRATION_ID_IDS.BTN_SUBMIT, window)}
        >
          {t(`${tPrefix}.buttons.next`)}
        </CallToActionButton>
      </HorizontalWrapper>
    );

    return (
      <ContainerTemplate
        title={tMerchantAware(`${tPrefix}.title`)}
        subtitle={tMerchantAware(`${tPrefix}.subtitle`)}
        error={error}
      >
        <SpacerVertical small />
        <ToggleWrappedContent
          error={ctaClicked && toggleValue === undefined}
          isDisabled={isDisabled}
          toggleOnChange={this.onToggle}
          toggleValue={toggleValue}
          yesContent={
            <>
              <Typography textToken="text-style/headings/blocks/bold/grande">
                {tMerchantAware(`${tPrefix}.form_title`)}
              </Typography>
              <SpacerVertical small />
              <FieldsetForObject
                name="company-registration-form"
                isDisabled={isDisabled}
                fields={fields}
                onBlur={this.onBlur}
                onChange={this.onChange}
              />
              <SpacerVertical large />
            </>
          }
          commonContent={actionButtons}
        />
      </ContainerTemplate>
    );
  }
}

OptionalCompanyRegistrationId.propTypes = {
  ...defaultContainerPropTypes,
  language: PropTypes.string.isRequired,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  merchant: PropTypes.object,
};

const WrappedComponent = compose(
  connect(state => ({
    defaultState: getDefaultState(state),
    language: i18nSelectors.language(state),
    t: countrySpecificTranslationParser(state),
    tMerchantAware: getMerchantAwareTranslator(state),
    countryCollectsDocuments: shouldCollectDocuments(state),
  })),
  branch(
    ({ countryCollectsDocuments }) => countryCollectsDocuments,
    withChainedCapabilitiesBeforeNext(
      {
        pathBusinessDetails: CAPS.V2_MERCHANTS_BUSINESS_DETAILS_PATCH,
        refreshDocuments: CAPS.V2_DOC_LIST,
      },
      {
        forceCapabilitiesOrder: ['pathBusinessDetails', 'refreshDocuments'],
        callback: ({ payload }) => {
          const fields = payload.body;
          logEvent(ANALYTICS_EVENTS.companyRegistrationInformation, fields);
        },
      },
    ),
    withChainedCapabilitiesBeforeNext(
      {
        pathBusinessDetails: CAPS.V2_MERCHANTS_BUSINESS_DETAILS_PATCH,
      },
      {
        forceCapabilitiesOrder: ['pathBusinessDetails'],
        callback: ({ payload }) => {
          const fields = payload.body;
          logEvent(ANALYTICS_EVENTS.companyRegistrationInformation, fields);
        },
      },
    ),
  ),
)(OptionalCompanyRegistrationId);

WrappedComponent.variantId = OPTIONS_META.REGISTRATION_ID_KIND.OPTIONS.OPTIONAL;
WrappedComponent.onboardingTypes = '*';

WrappedComponent.fieldsToFill = [
  {
    fieldName: 'businessDetails',
    fields: {
      registered: {},
      // registrationId is optional only if registered is false.
      registrationId: { optional: isRegistrationIdOptional },
    },
  },
];

export default WrappedComponent;
